import { Alert, Badge, Button, Card, EmptyState, Field, Input, Skeleton } from '@personachat/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { useId, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { api } from '../../api/client';
import { lire, messageDe } from '../../api/call';
import type { components } from '../../api/schema';
import { Code, ErreurChargement, InfoList, Jauge, Section, Select } from '../../components/bits';
import { Drawer } from '../../components/Drawer';
import { ConfirmDialog } from '../../components/Modal';
import { TabPanel, Tabs } from '../../components/Tabs';
import { useToast } from '../../components/Toast';
import { depuisSaisieParis, formatDateTime, formatNombre, saisieParis } from '../../lib/format';
import { LIBELLE_TYPE, TON_TYPE } from '../../lib/labels';
import { useSession } from '../../auth/AuthProvider';
import { AuditTimeline } from '../audit/AuditTimeline';

import { periode, StatutEvenement, useModules } from './EventsPage';

type Fiche = components['schemas']['EventDetail'];
type Onglet = 'reglages' | 'calendrier' | 'fin' | 'acces' | 'journal';
const ONGLETS: Array<{ id: Onglet; label: string }> = [
  { id: 'reglages', label: 'Réglages' },
  { id: 'calendrier', label: 'Calendrier' },
  { id: 'fin', label: 'Fin de l’événement' },
  { id: 'acces', label: 'Accès' },
  { id: 'journal', label: 'Journal' },
];

/** Fiche d'un événement (cahier des charges §7.6), en panneau : /evenements/:id. */
export function EventDrawer() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const onglet = (ONGLETS.some((o) => o.id === params.get('onglet')) ? params.get('onglet') : 'reglages') as Onglet;

  const fiche = useQuery({
    queryKey: ['evenement', id],
    queryFn: async () => (await lire(api.GET('/api/admin/events/{id}', { params: { path: { id } } }))).event,
  });
  const e = fiche.data;
  const fermer = () => navigate('/evenements');

  return (
    <Drawer
      onClose={fermer}
      label={e ? `Événement ${e.name}` : 'Événement'}
      title={e ? e.name : <Skeleton width={200} height={22} />}
      subtitle={e ? `Module ${e.module.name} · ${periode(e)}` : undefined}
      badges={
        e ? (
          <>
            <Badge tone={TON_TYPE[e.userType]}>Comptes {LIBELLE_TYPE[e.userType]}</Badge>
            <StatutEvenement statut={e.status} />
            {e.endBehavior === 'BLOCK' && <Badge tone="terracotta">Bloqué après la fin</Badge>}
          </>
        ) : undefined
      }
    >
      {fiche.isError ? (
        <div style={{ paddingTop: 16 }}>
          <ErreurChargement erreur={fiche.error} onReessayer={() => void fiche.refetch()} />
        </div>
      ) : !e ? (
        <Skeleton height={240} />
      ) : (
        <>
          <Tabs
            label="Sections de l’événement"
            actif={onglet}
            onChange={(o) =>
              setParams(
                (a) => {
                  const s = new URLSearchParams(a);
                  if (o === 'reglages') s.delete('onglet');
                  else s.set('onglet', o);
                  return s;
                },
                { replace: true }
              )
            }
            onglets={ONGLETS.map((o) => (o.id === 'calendrier' ? { ...o, count: e._count.schedule } : o))}
          />
          <TabPanel label={ONGLETS.find((o) => o.id === onglet)!.label}>
            <NoteLecture />
            {/* Formulaires remontés quand l'événement change : ils repartent des valeurs enregistrées. */}
            {onglet === 'reglages' && <Reglages key={e.updatedAt} fiche={e} onSupprime={fermer} />}
            {onglet === 'calendrier' && <Calendrier key={e.coaches.map((c) => c.opensAt ?? '').join('|')} fiche={e} />}
            {onglet === 'fin' && <Fin key={e.updatedAt} fiche={e} />}
            {onglet === 'acces' && <Acces fiche={e} />}
            {onglet === 'journal' && <AuditTimeline targetId={e.id} />}
          </TabPanel>
        </>
      )}
    </Drawer>
  );
}

/** Seul le super admin modifie un événement (cahier des charges §3) ; l'admin consulte. */
function useEcriture(): boolean {
  return !!useSession().user?.isSuperAdmin;
}

function NoteLecture() {
  if (useEcriture()) return null;
  return <p className="note">Consultation seule : seul un super admin modifie un événement.</p>;
}

function useRafraichir(id: string) {
  const queryClient = useQueryClient();
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['evenement', id] }),
      queryClient.invalidateQueries({ queryKey: ['evenements'] }),
      queryClient.invalidateQueries({ queryKey: ['audit', id] }),
    ]);
}

function Chiffres({ fiche: e }: { fiche: Fiche }) {
  return (
    <div className="kpis kpis--compact" style={{ marginBottom: 16 }}>
      <Card className="kpi">
        <div className="kpi__label">Inscrites</div>
        <div className="kpi__value">{formatNombre(e.stats.registered)}</div>
      </Card>
      <Card className="kpi">
        <div className="kpi__label">Actives (7 j)</div>
        <div className="kpi__value">{formatNombre(e.stats.active7d)}</div>
      </Card>
      <Card className="kpi">
        <div className="kpi__label">Passées en Normal</div>
        <div className="kpi__value">{formatNombre(e.stats.movedToNormal)}</div>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Réglages
// ---------------------------------------------------------------------------

function Reglages({ fiche: e, onSupprime }: { fiche: Fiche; onSupprime: () => void }) {
  const id = useId();
  const toast = useToast();
  const queryClient = useQueryClient();
  const rafraichir = useRafraichir(e.id);
  const ecriture = useEcriture();
  const modules = useModules();
  const [nom, setNom] = useState(e.name);
  const [moduleId, setModuleId] = useState(e.moduleId);
  const [debut, setDebut] = useState(saisieParis(e.startsAt));
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [supprimer, setSupprimer] = useState(false);

  const debutIso = debut ? depuisSaisieParis(debut) : null;
  const changements = {
    ...(nom.trim() !== e.name ? { name: nom.trim() } : {}),
    ...(moduleId !== e.moduleId ? { moduleId } : {}),
    ...(debut !== saisieParis(e.startsAt) ? { startsAt: debutIso } : {}),
  };
  const aChange = Object.keys(changements).length > 0;
  const nomValide = nom.trim().length >= 2;

  const enregistrer = async (ev: FormEvent) => {
    ev.preventDefault();
    if (!aChange || !nomValide) return;
    setEnvoi(true);
    setErreur(null);
    try {
      await lire(api.PATCH('/api/admin/events/{id}', { params: { path: { id: e.id } }, body: changements }));
      await rafraichir();
      toast.succes(changements.moduleId ? 'Événement modifié : le calendrier du nouveau module est à régler.' : 'Événement modifié.');
    } catch (err) {
      setErreur(messageDe(err));
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <>
      <Chiffres fiche={e} />
      <Section title="Réglages">
        <form className="stack" onSubmit={enregistrer}>
          <fieldset className="fieldset-nu stack" disabled={!ecriture}>
          <Field id={`${id}-nom`} label="Nom" error={nomValide ? null : '2 caractères au moins.'}>
            <Input id={`${id}-nom`} value={nom} onChange={(ev) => setNom(ev.target.value)} maxLength={80} invalid={!nomValide} />
          </Field>
          <Field id={`${id}-module`} label="Module ouvert aux inscrites" hint="Tous ses coachs actifs sont débloqués à l’inscription.">
            <Select id={`${id}-module`} value={moduleId} onChange={(ev) => setModuleId(ev.target.value)}>
              {!(modules.data ?? []).some((m) => m.id === e.moduleId) && <option value={e.moduleId}>{e.module.name}</option>}
              {(modules.data ?? []).map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                  {m.isActive ? '' : ' (inactif)'}
                </option>
              ))}
            </Select>
          </Field>
          {moduleId !== e.moduleId && (
            <Alert tone="info">
              Changer de module efface le calendrier actuel ({formatNombre(e._count.schedule)} date{e._count.schedule > 1 ? 's' : ''}). Les coachs du
              nouveau module sont ouverts aux {formatNombre(e.stats.registered)} inscrites ; l’application ne leur montre plus l’ancien.
            </Alert>
          )}
          <Field id={`${id}-debut`} label="Début (heure de Paris)" hint="Vide : dès l’inscription. La fin se règle dans l’onglet « Fin de l’événement ».">
            <Input id={`${id}-debut`} type="datetime-local" value={debut} onChange={(ev) => setDebut(ev.target.value)} />
          </Field>
          </fieldset>
          <InfoList items={[{ label: 'Comptes concernés', value: `${LIBELLE_TYPE[e.userType]} (définitif)` }]} />
          {erreur && <Alert>{erreur}</Alert>}
          {ecriture && (
            <div className="row">
              <Button type="submit" variant="primary" loading={envoi} disabled={!aChange || !nomValide}>
                Enregistrer
              </Button>
            </div>
          )}
        </form>
      </Section>

      {ecriture && e._count.accessKeys === 0 && (
        <Section title="Supprimer">
          <p className="note">Aucune clé d’accès n’est rattachée à cet événement : il peut encore être supprimé.</p>
          <Button variant="danger" icon={<Trash2 size={15} aria-hidden="true" />} onClick={() => setSupprimer(true)}>
            Supprimer l’événement
          </Button>
        </Section>
      )}

      <ConfirmDialog
        open={supprimer}
        onOpenChange={setSupprimer}
        title="Supprimer l’événement"
        confirmLabel="Supprimer"
        tone="danger"
        retaper={e.name}
        onConfirm={async () => {
          await lire(api.DELETE('/api/admin/events/{id}', { params: { path: { id: e.id } } }));
          await queryClient.invalidateQueries({ queryKey: ['evenements'] });
          toast.succes('Événement supprimé.');
          onSupprime();
        }}
      >
        <p>L’événement « {e.name} » et son calendrier sont supprimés définitivement.</p>
      </ConfirmDialog>
    </>
  );
}

// ---------------------------------------------------------------------------
// Calendrier
// ---------------------------------------------------------------------------

function Calendrier({ fiche: e }: { fiche: Fiche }) {
  const toast = useToast();
  const rafraichir = useRafraichir(e.id);
  const ecriture = useEcriture();
  const initial = () => Object.fromEntries(e.coaches.map((c) => [c.id, saisieParis(c.opensAt)]));
  const [dates, setDates] = useState<Record<string, string>>(initial);
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  const aChange = e.coaches.some((c) => (dates[c.id] ?? '') !== saisieParis(c.opensAt));
  const invalides = e.coaches.filter((c) => dates[c.id] && !depuisSaisieParis(dates[c.id]!));
  const maintenant = Date.now();

  const enregistrer = async (ev: FormEvent) => {
    ev.preventDefault();
    if (!aChange || invalides.length) return;
    setEnvoi(true);
    setErreur(null);
    try {
      const items = e.coaches.map((c) => ({ personalityId: c.id, opensAt: dates[c.id] ? depuisSaisieParis(dates[c.id]!) : null }));
      await lire(api.PUT('/api/admin/events/{id}/schedule', { params: { path: { id: e.id } }, body: { items } }));
      await rafraichir();
      toast.succes('Calendrier enregistré.');
    } catch (err) {
      setErreur(messageDe(err));
    } finally {
      setEnvoi(false);
    }
  };

  if (e.coaches.length === 0) {
    return <EmptyState title="Aucun coach dans ce module" text={`Ajoutez des coachs au module « ${e.module.name} » pour régler leur ouverture.`} />;
  }

  return (
    <form className="stack" onSubmit={enregistrer}>
      <p className="note">
        Date et heure d’ouverture de chaque coach, à l’heure de Paris. Sans date, le coach est ouvert dès le début. La clé de prévisualisation
        ignore le calendrier.
      </p>
      <fieldset className="fieldset-nu calendrier" disabled={!ecriture} aria-label="Ouverture des coachs">
        {e.coaches.map((c) => {
          const iso = dates[c.id] ? depuisSaisieParis(dates[c.id]!) : null;
          const ouvert = !iso || new Date(iso).getTime() <= maintenant;
          return (
            <div key={c.id} className="calendrier__ligne">
              <div className="calendrier__coach">
                <label className="cell-main" htmlFor={`ouverture-${c.id}`}>
                  {c.name}
                </label>
                <div className="cell-sub">
                  {!c.isActive ? 'Inactif : non proposé aux élèves' : !dates[c.id] ? 'Ouvert dès le début' : ouvert ? 'Déjà ouvert' : `Ouvre le ${formatDateTime(iso)}`}
                </div>
              </div>
              <Input
                id={`ouverture-${c.id}`}
                type="datetime-local"
                value={dates[c.id] ?? ''}
                onChange={(ev) => setDates((d) => ({ ...d, [c.id]: ev.target.value }))}
                invalid={invalides.includes(c)}
              />
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setDates((d) => ({ ...d, [c.id]: '' }))}
                disabled={!dates[c.id]}
                aria-label={`Retirer la date de ${c.name}`}
              >
                Sans date
              </Button>
            </div>
          );
        })}
      </fieldset>
      {erreur && <Alert>{erreur}</Alert>}
      {ecriture && (
        <div className="row">
          <Button type="submit" variant="primary" loading={envoi} disabled={!aChange || invalides.length > 0}>
            Enregistrer le calendrier
          </Button>
          {aChange && (
            <Button variant="ghost" onClick={() => setDates(initial())}>
              Annuler
            </Button>
          )}
        </div>
      )}
    </form>
  );
}

// ---------------------------------------------------------------------------
// Fin de l'événement
// ---------------------------------------------------------------------------

const MESSAGE_PAR_DEFAUT = 'Cet événement est terminé. Merci pour votre participation !';

function Fin({ fiche: e }: { fiche: Fiche }) {
  const id = useId();
  const toast = useToast();
  const rafraichir = useRafraichir(e.id);
  const ecriture = useEcriture();
  const [fin, setFin] = useState(saisieParis(e.endsAt));
  const [comportement, setComportement] = useState(e.endBehavior);
  const [message, setMessage] = useState(e.endMessage ?? '');
  const [bouton, setBouton] = useState(e.endCtaLabel ?? '');
  const [lien, setLien] = useState(e.endCtaUrl ?? '');
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  const finIso = fin ? depuisSaisieParis(fin) : null;
  const erreurs = {
    fin: finIso && e.startsAt && finIso <= new Date(e.startsAt).toISOString() ? 'La fin doit être après le début.' : null,
    lien: lien.trim() && !/^https:\/\/\S+$/.test(lien.trim()) ? 'Le lien doit commencer par https://' : null,
    bouton: lien.trim() && !bouton.trim() ? 'Donnez un texte au bouton.' : bouton.trim() && !lien.trim() ? 'Donnez le lien du bouton.' : null,
  };
  const valide = Object.values(erreurs).every((x) => x === null);
  const changements = {
    ...(fin !== saisieParis(e.endsAt) ? { endsAt: finIso } : {}),
    ...(comportement !== e.endBehavior ? { endBehavior: comportement } : {}),
    ...(message.trim() !== (e.endMessage ?? '') ? { endMessage: message.trim() || null } : {}),
    ...(bouton.trim() !== (e.endCtaLabel ?? '') ? { endCtaLabel: bouton.trim() || null } : {}),
    ...(lien.trim() !== (e.endCtaUrl ?? '') ? { endCtaUrl: lien.trim() || null } : {}),
  };
  const aChange = Object.keys(changements).length > 0;
  const bloqueDesMaintenant = comportement === 'BLOCK' && finIso !== null && new Date(finIso).getTime() <= Date.now();

  const enregistrer = async (ev: FormEvent) => {
    ev.preventDefault();
    if (!aChange || !valide) return;
    setEnvoi(true);
    setErreur(null);
    try {
      await lire(api.PATCH('/api/admin/events/{id}', { params: { path: { id: e.id } }, body: changements }));
      await rafraichir();
      toast.succes('Fin de l’événement enregistrée.');
    } catch (err) {
      setErreur(messageDe(err));
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <form className="stack" onSubmit={enregistrer} noValidate>
      <fieldset className="fieldset-nu stack" disabled={!ecriture}>
      <Field id={`${id}-fin`} label="Fin (heure de Paris)" hint="Vide : l’événement ne se termine pas." error={erreurs.fin}>
        <Input id={`${id}-fin`} type="datetime-local" value={fin} onChange={(ev) => setFin(ev.target.value)} invalid={!!erreurs.fin} />
      </Field>
      <fieldset className="choix">
        <legend className="ui-field__label">Après la fin</legend>
        <label className={comportement === 'OPEN' ? 'choix__option choix__option--active' : 'choix__option'}>
          <input type="radio" name={`${id}-comportement`} checked={comportement === 'OPEN'} onChange={() => setComportement('OPEN')} />
          <span>
            <strong>Laisser ouvert</strong>
            <span className="note">Les inscrites continuent d’utiliser l’application ; l’écran de fin est seulement proposé.</span>
          </span>
        </label>
        <label className={comportement === 'BLOCK' ? 'choix__option choix__option--active' : 'choix__option'}>
          <input type="radio" name={`${id}-comportement`} checked={comportement === 'BLOCK'} onChange={() => setComportement('BLOCK')} />
          <span>
            <strong>Bloquer</strong>
            <span className="note">
              L’application affiche l’écran de fin ; nouveaux messages et générations refusés. Les inscrites peuvent toujours passer en accès
              complet.
            </span>
          </span>
        </label>
      </fieldset>
      {bloqueDesMaintenant && (
        <Alert>
          La date de fin est passée : dès l’enregistrement, les {formatNombre(e.stats.registered)} inscrites de l’événement sont bloquées.
        </Alert>
      )}

      <Section title="Écran de fin">
        <div className="stack">
          <Field id={`${id}-message`} label="Message" hint={`Vide : « ${MESSAGE_PAR_DEFAUT} »`}>
            <textarea
              id={`${id}-message`}
              className="ui-input textarea textarea--texte"
              value={message}
              onChange={(ev) => setMessage(ev.target.value)}
              maxLength={1000}
              placeholder={MESSAGE_PAR_DEFAUT}
            />
          </Field>
          <div className="grid-2">
            <Field id={`${id}-bouton`} label="Texte du bouton (facultatif)" error={erreurs.bouton}>
              <Input id={`${id}-bouton`} value={bouton} onChange={(ev) => setBouton(ev.target.value)} maxLength={60} placeholder="Passer au programme complet" invalid={!!erreurs.bouton} />
            </Field>
            <Field id={`${id}-lien`} label="Lien du bouton" error={erreurs.lien}>
              <Input id={`${id}-lien`} value={lien} onChange={(ev) => setLien(ev.target.value)} maxLength={500} placeholder="https://…" invalid={!!erreurs.lien} />
            </Field>
          </div>
          <div>
            <div className="ui-field__label" style={{ marginBottom: 6 }}>
              Aperçu
            </div>
            <div className="ecran-fin" role="img" aria-label="Aperçu de l’écran de fin">
              <strong>{e.name}</strong>
              <div className="ecran-fin__message">{message.trim() || MESSAGE_PAR_DEFAUT}</div>
              {bouton.trim() && <span className="ecran-fin__bouton">{bouton.trim()}</span>}
            </div>
          </div>
        </div>
      </Section>

      </fieldset>
      {erreur && <Alert>{erreur}</Alert>}
      {ecriture && (
        <div className="row">
          <Button type="submit" variant="primary" loading={envoi} disabled={!aChange || !valide}>
            Enregistrer
          </Button>
        </div>
      )}
    </form>
  );
}

// ---------------------------------------------------------------------------
// Accès : clé de prévisualisation et clés d'inscription
// ---------------------------------------------------------------------------

function Acces({ fiche: e }: { fiche: Fiche }) {
  const toast = useToast();
  const rafraichir = useRafraichir(e.id);
  const queryClient = useQueryClient();
  const [generer, setGenerer] = useState(false);
  const ecriture = useEcriture();
  const cle = e.previewAccessKey;

  return (
    <>
      <Section title="Clé de prévisualisation">
        <p className="note" style={{ marginTop: 0 }}>
          Pour tester l’événement avant son ouverture : une inscription avec cette clé crée un compte {LIBELLE_TYPE[e.userType]} rattaché à
          l’événement, qui voit tous les coachs du module sans attendre leur date.
        </p>
        {cle ? (
          <InfoList
            items={[
              { label: 'Code', value: <Code valeur={cle.key} /> },
              { label: 'Inscriptions', value: <Jauge utilise={cle.usedCount} max={cle.maxUses} /> },
              { label: 'État', value: cle.isActive ? <Badge tone="success">Active</Badge> : <Badge>Désactivée</Badge> },
              {
                label: 'Fiche de la clé',
                value: (
                  <Link className="link" to={`/cles-acces/${cle.id}`}>
                    Ouvrir
                  </Link>
                ),
              },
            ]}
          />
        ) : e.previewKey ? (
          <p>
            Clé historique <Code valeur={e.previewKey} /> (antérieure au back-office).
          </p>
        ) : (
          <p className="muted">Aucune clé de prévisualisation.</p>
        )}
        {ecriture && (
          <div className="row" style={{ marginTop: 10 }}>
            <Button icon={<RefreshCw size={15} aria-hidden="true" />} onClick={() => setGenerer(true)}>
              {cle || e.previewKey ? 'Générer une nouvelle clé' : 'Générer une clé'}
            </Button>
          </div>
        )}
      </Section>

      <Section title="Clés d’inscription">
        <p className="note" style={{ marginTop: 0 }}>
          {formatNombre(e._count.accessKeys)} clé{e._count.accessKeys > 1 ? 's' : ''} d’accès rattachée{e._count.accessKeys > 1 ? 's' : ''} à cet
          événement (prévisualisation comprise).
        </p>
        <div className="row">
          <Link className="ui-btn ui-btn--secondary" to={`/cles-acces?evenement=${e.id}`}>
            <KeyRound size={15} aria-hidden="true" />
            Voir les clés
          </Link>
          <Link className="ui-btn ui-btn--secondary" to={`/cles-acces?creer=1&evenement=${e.id}`}>
            <Plus size={15} aria-hidden="true" />
            Créer des clés pour cet événement
          </Link>
        </div>
      </Section>

      <ConfirmDialog
        open={generer}
        onOpenChange={setGenerer}
        title="Nouvelle clé de prévisualisation"
        confirmLabel="Générer"
        onConfirm={async () => {
          const r = await lire(api.POST('/api/admin/events/{id}/preview-key', { params: { path: { id: e.id } } }));
          await Promise.all([rafraichir(), queryClient.invalidateQueries({ queryKey: ['cles-acces'] })]);
          toast.succes(`Clé ${r.previewAccessKey.key} créée.`);
        }}
      >
        <p>
          Une nouvelle clé (20 inscriptions) est créée.
          {(cle || e.previewKey) && (
            <>
              {' '}
              L’ancienne, <code>{cle?.key ?? e.previewKey}</code>, est désactivée : elle ne permet plus de s’inscrire.
            </>
          )}
        </p>
      </ConfirmDialog>
    </>
  );
}
